import React, {useEffect, useState} from "react";
import {NavigateFunction, useNavigate, useParams} from "react-router-dom";
import * as Yup from 'yup';
import {useFormik} from 'formik';
import {FormControl, InputLabel, Link, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import {ArrowBackIosNew} from "@mui/icons-material";
import showNotification from "../../utils/Notify";
import {Box, Breadcrumbs, Button, Grid, Stack, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {create, getById, update} from "../../../services/revision-item.service";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import IRevisionItem from "../../../types/revision-item.type";
import {RevisionItemCategory} from "../../../types/enumeration/RevisionItemCategory";
import {RevisionItemCategoryNames} from "../../../types/enumeration/RevisionItemCategoryNames";

const RevisionItemForm: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const { id } = useParams();

  const initialValues: IRevisionItem = {
    id: null,
    description: '',
    active: 'true',
    category: RevisionItemCategory.BASIC,
    weighing: 0
  };

  const [revisionItem, setRevisionItem] = useState(initialValues);

  const validationSchema = Yup.object({
    description: Yup.string().required('La descripcion es un campo requerido'),
    category: Yup.string().required('La categoria es un campo requerido'),
    weighing: Yup.string().required('La ponderacion es un campo requerido'),
    active: Yup.string().required('Es necesario indicar si la Categoria esta activa'),
  });

  const onSubmit = (revisionItem: IRevisionItem) => {
    if (id) {
      update(revisionItem.id, revisionItem)
        .then(() => {
          showNotification({type: 'success', message: 'Item de Revision actualizado correctamente.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(revisionItem)
        .then((response) => {
          showNotification({type: 'success', message: 'Item de Revision creado correctamente.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const goBack = () => {
    navigate(-1)
  };

  const handleChangeCategory = (event: SelectChangeEvent) => {
    formik.setFieldValue('category', event.target.value);
  };

  const handleChangeActive = (event: SelectChangeEvent) => {
    formik.setFieldValue('active', event.target.value)
  };

  const fetchRevisionItem = async () => {
    if (id) {
      getById(id).then( (response) => {
        let itemRevisionData = response.data;
        formik.setValues(itemRevisionData)
        setRevisionItem(itemRevisionData)
      })
    }
  };

  useEffect(() => {
    fetchRevisionItem();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <AccountTreeIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => goBack()}
                  sx={{ cursor: 'pointer' }}
                >
                  Items de Revision
                </Link>
              </Typography>
              <Typography level="h4" component="h1">
                {id ? "Editar" : "Nuevo"}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <form className="form" onSubmit={formik.handleSubmit}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                marginBlock={1}
                marginInline={0}
                sx={{ width: '100%' }}
              >
                <Grid xl={6} md={6} sm={6} xs={6} container>
                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <TextField
                        fullWidth
                        id="description"
                        label="Descripcion"
                        name="description"
                        variant="filled"
                        type={"text"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.description}
                        error={!!(formik.touched.description && formik.errors.description)}
                        multiline
                        rows={8}
                      />
                      {formik.touched.description && formik.errors.description ? (
                        <span className="Error-alert">{formik.errors.description}</span>
                      ) : null}
                    </CssVarsProvider>
                  </Grid>
                </Grid>
                <Grid xl={6} md={6} sm={6} xs={6} container>
                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <FormControl variant="filled" fullWidth>
                        <InputLabel id="type-label" shrink>Categoria</InputLabel>
                        <Select
                          labelId="type-label"
                          id="type"
                          name="type"
                          label="Tipo"
                          value={formik.values.category}
                          onBlur={formik.handleBlur}
                          onChange={handleChangeCategory}
                        >
                          {Object
                            .keys(RevisionItemCategory)
                            .map((key) => (
                              <MenuItem key={key} value={RevisionItemCategory[key as keyof typeof RevisionItemCategory]}>
                                {RevisionItemCategoryNames[RevisionItemCategory[key as keyof typeof RevisionItemCategory]]}
                              </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.category && formik.errors.category ? (
                          <span className="Error-alert">{String(formik.errors.category)}</span>
                        ) : null}
                      </FormControl>
                    </CssVarsProvider>
                  </Grid>
                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <TextField
                        fullWidth
                        id="weighing"
                        label="Ponderacion"
                        name="weighing"
                        variant="filled"
                        type={"number"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.weighing}
                        error={!!(formik.touched.weighing && formik.errors.weighing)}
                        slotProps={{ inputLabel: {shrink: true}}}
                      />
                      {formik.touched.weighing && formik.errors.weighing ? (
                        <span className="Error-alert">{formik.errors.weighing}</span>
                      ) : null}
                    </CssVarsProvider>
                  </Grid>
                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <FormControl variant="filled" fullWidth>
                        <InputLabel id="lockCalendar-label" shrink>Activo</InputLabel>
                        <Select
                          labelId="enabled-label"
                          id="enabled"
                          name="enabled"
                          label="enabled"
                          value={formik.values.active}
                          onBlur={formik.handleBlur}
                          onChange={handleChangeActive}
                        >
                          <MenuItem key="true" value="true">Si</MenuItem>
                          <MenuItem key="false" value="false">No</MenuItem>
                        </Select>
                        {formik.touched.active && formik.errors.active ? (
                          <span className="Error-alert">{formik.errors.active}</span>
                        ) : null}
                      </FormControl>
                    </CssVarsProvider>
                  </Grid>
                </Grid>
              </Grid>

              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
                <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
              </Stack>
            </form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RevisionItemForm;
